// Verdicts (U0), pure parts: what `!` is about on each screen, which verdicts it offers, and the
// request it sends. A verdict says what MeeMail got wrong; it never changes a card or Gmail.
import type { CardBlock } from "@shared/core";
import type { HiddenThreadView, RoutineGroupView } from "@shared/api";
import {
  VERDICT_NOTE_MAX,
  VERDICTS,
  type Verdict,
  type VerdictRequest,
  type VerdictSurface,
} from "@shared/measure";

export interface VerdictThread {
  threadKey: string;
  subject: string;
}

export interface VerdictTarget {
  surface: VerdictSurface;
  /** What the verdict can be about: one thread for a card or row, each thread of a cleanup. */
  threads: VerdictThread[];
  cardId?: string;
  groupId?: string;
  /** "Draft needed rewriting" is offered only where a draft exists. */
  hasDraft: boolean;
  /** The dialog's heading: the card title, the subject, or the cleanup's label. */
  title: string;
}

const subjectOf = (s: string | null | undefined) => s?.trim() || "(no subject)";

export function verdictChoices(target: VerdictTarget): Verdict[] {
  return VERDICTS.filter((v) => v !== "draft_rewrite" || target.hasDraft);
}

export function verdictRequest(
  target: VerdictTarget,
  verdict: Verdict,
  threadKey: string,
  note: string,
): VerdictRequest {
  const text = note.trim().slice(0, VERDICT_NOTE_MAX);
  return {
    threadKey,
    verdict,
    surface: target.surface,
    ...(target.cardId ? { cardId: target.cardId } : {}),
    ...(target.groupId ? { groupId: target.groupId } : {}),
    ...(text ? { note: text } : {}),
  };
}

/** A decision card (feed, card page, focus). `blocks` unknown (a list row): no draft verdict. */
export function cardTarget(
  card: { id: string; threadKey: string | null; title: string; blocks?: readonly CardBlock[] },
  surface: "card" | "focus",
): VerdictTarget | null {
  if (!card.threadKey) return null;
  return {
    surface,
    threads: [{ threadKey: card.threadKey, subject: card.title }],
    cardId: card.id,
    hasDraft: (card.blocks ?? []).some((b) => b.type === "editable_text"),
    title: card.title,
  };
}

/** An inbox row or an open thread (no card in view). */
export function threadTarget(
  thread: { key: string; subject: string | null },
  surface: "inbox" | "thread",
): VerdictTarget {
  const subject = subjectOf(thread.subject);
  return {
    surface,
    threads: [{ threadKey: thread.key, subject }],
    hasDraft: false,
    title: subject,
  };
}

/** A suggested cleanup: the verdict is about one of its threads (the first unless chosen). */
export function groupTarget(group: RoutineGroupView): VerdictTarget | null {
  const threads = group.items.map((i) => ({
    threadKey: i.threadKey,
    subject: subjectOf(group.threads[i.threadKey]?.subject),
  }));
  if (!threads.length) return null;
  return { surface: "routine", threads, groupId: group.id, hasDraft: false, title: group.label };
}

/** A row of "What I set aside and why". */
export function hiddenTarget(item: HiddenThreadView): VerdictTarget {
  const subject = subjectOf(item.thread?.subject);
  return {
    surface: "set_aside",
    threads: [{ threadKey: item.threadKey, subject }],
    ...(item.cardId ? { cardId: item.cardId } : {}),
    hasDraft: false,
    title: subject,
  };
}
